<script setup></script>

<template>
  <div class="sidenav-menu">
    <!-- Add Debug Panel toggle button in admin view -->
    <div v-if="isAdminView && isInChatPage && !isMobile" class="debug-toggle">
      <label class="debug-toggle-switch">
        <input
          type="checkbox"
          v-model="session.debugPanelOpen"
        />
        <span class="toggle-slider round"></span>
        <span class="toggle-label">Debug Panel</span>
      </label>
    </div>

    <i
      v-if="isMobile && !dropdownOpen"
      class="fas fa-bars menu-icon"
      @click="toggleDropdown"
    ></i>
    <div
      v-if="!isMobile || dropdownOpen"
      class="sidenav-content"
      @click.self="closeNavMenu"
    >
      <div v-if="isUserView" class="mentee-view">
        <nav>
          <h6>Coaching</h6>
          <div v-if="loading" class="loading-message">Chats loading...</div>
          <div v-else>
            <a
              href="/chat"
              @click.prevent="
                selectCurrentChat();
                handleNavClick();
              "
            >
              <i class="fas fa-comments"></i>
              Current Session
            </a>
            <div v-if="chatHistory.length">
              <div v-for="chat in chatHistory" :key="chat.id">
                <i class="fas fa-file-alt icon-spacing"> </i>
                <a
                  :href="'/chat/' + chat.sessionDate"
                  @click.prevent="
                    selectChatHistory(chat);
                    handleNavClick();
                  "
                >
                  {{ chat.label }}
                </a>
              </div>
            </div>
          </div>
        </nav>
        <hr class="divider" />
      </div>
      <div v-if="isPeerMentorView" class="mentor-view">
        <nav>
          <h6>Peer Management</h6>
          <a href="/mentees" @click="handleNavClick()">Peer Mentees</a>
          <!-- a href="/menteesuggestion" @click="handleNavClick()">Provide Suggestions</a -->
        </nav>
        <hr class="divider" />
      </div>
      <div v-if="isAdminView && !isMobile" class="admin-view">
        <nav>
          <h6>Administration</h6>
          <a href="/users">User Management</a>
          <!-- Data and Monitor are hidden until their pages are implemented.
               Routes, components and role guards are all left in place, so
               restoring these two links is the only step needed. -->
          <!-- a href="/data">Data</a -->
          <a href="/configuration">Configuration</a>
          <!-- a href="/monitor">Monitor</a -->
        </nav>
        <hr class="divider" />
      </div>
      <div>
        <nav>
          <a href="/settings" class="settings" @click="handleNavClick()"
            ><i class="fas fa-cog"></i>Settings</a
          >
          <router-link to="/reset-password" class="nav-item"
            ><i class="bi bi-key"></i>
            <span>Reset Password</span>
          </router-link>
        </nav>
        <hr class="divider" />
      </div>
      <div>
        <nav>
          <form @submit.prevent="handleLogout">
            <button type="submit">Logout</button>
          </form>
          <a href="#" class="close-nav" @click="closeNavMenu()">X Close</a>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useSessionStore } from "@/stores/session";
import { useRouter } from "vue-router";
import { format } from "date-fns";
import authApi from "@/services/auth_api";

export default {
  setup() {
    const session = useSessionStore();
    const router = useRouter();
    const isMobile = ref(false);
    const dropdownOpen = ref(false);
    const currentSessionId = ref("");
    // Reactive view of the store's past sessions so the sidebar refreshes
    // whenever the store updates (e.g. after a force-end flips newSessionNeeded).
    // Sessions are newest-first. When a week (sessionDate) holds more than one
    // session, append an incremental index so demo runs are distinguishable;
    // the newest gets the highest number and no suffix is shown for lone weeks.
    const chatHistory = computed(() => {
      const sessions = session.chatHistory || [];
      const counts = {};
      for (const c of sessions) {
        counts[c.sessionDate] = (counts[c.sessionDate] || 0) + 1;
      }
      const seen = {};
      return sessions.map((c) => {
        const total = counts[c.sessionDate];
        let label = c.sessionDate;
        if (total > 1) {
          const n = total - (seen[c.sessionDate] || 0); // newest -> highest
          seen[c.sessionDate] = (seen[c.sessionDate] || 0) + 1;
          label = `${c.sessionDate} (${n})`;
        }
        return { ...c, label };
      });
    });
    const isUserView = ref(false);
    const isPeerMentorView = ref(false);
    const isAdminView = ref(false);
    const loading = ref(true);
    const isInChatPage = ref(false);

    watch(
      () => router.currentRoute.value.path,
      (path) => {
        isInChatPage.value = path === "/chat" || path.startsWith("/chat/");
      },
      { immediate: true },
    );

    const closeNavMenu = () => {
      console.log("closeNavMenu");
      dropdownOpen.value = false;
      document.body.style.overflow = "";
    };

    const handleNavClick = () => {
      console.log("outside Mobile view check");
      if (isMobile.value) {
        console.log("Mobile view");
        // closeNavMenu();
      }
    };

    const selectView = (view) => {
      closeNavMenu();
      if (view === "user") {
        showUserView();
      } else if (view === "peerMentor") {
        showPeerMentorView();
      } else if (view === "admin") {
        showAdminView();
      }
    };
    const showUserView = () => {
      console.log("User view");
      isUserView.value = true;
    };

    const showPeerMentorView = () => {
      console.log("Peer mentor view");
      isPeerMentorView.value = true;
    };

    const showAdminView = () => {
      console.log("Admin view");
      isAdminView.value = true;
    };

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    const toggleDropdown = () => {
      dropdownOpen.value = !dropdownOpen.value;
      // lock body scroll when the drawer is open
      document.body.style.overflow = dropdownOpen.value ? "hidden" : "";
    };

    const fetchChatHistory = async () => {
      // Simulate loading
      // await new Promise(resolve => setTimeout(resolve, 2000));
      try {
        await session.fetchChatHistory();
        currentSessionId.value = session.currentSessionId;
        // chatHistory is a computed off the store; no manual copy needed.
      } catch (error) {
        console.error("Error fetching chat history:", error);
      } finally {
        loading.value = false;
      }
    };

    const formatSessionDate = (sessionDate) => {
      // Extract the date components from the string
      const [start, end] = sessionDate.split("-");
      const [startYear, startMonth, startDay] = start.split(".").map(Number);
      const [endYear, endMonth, endDay] = end.split(".").map(Number);

      // Create Date objects
      const startDate = new Date(2000 + startYear, startMonth - 1, startDay);
      const endDate = new Date(2000 + endYear, endMonth - 1, endDay);

      // Format the dates
      const formattedStartDate = format(startDate, "MMMM dd, yyyy");
      const formattedEndDate = format(endDate, "MMMM dd, yyyy");

      // Return the formatted date range
      return `${formattedStartDate} - ${formattedEndDate}`;
    };

    const selectChatHistory = (chat) => {
      closeNavMenu();
      session.isViewingCurrentChat = false;
      session.selectedChatHistory = chat.history;
      session.selectedSummary = chat.summary_history;
    };

    const selectCurrentChat = () => {
      closeNavMenu();
      session.isViewingCurrentChat = true;
      session.selectedChatHistory = session.currentChatHistory;
      router.push("/chat");
    };

    onMounted(() => {
      checkMobile();
      window.addEventListener("resize", checkMobile);
      fetchChatHistory();
      if (session.rolesIncludes("mentee") || session.rolesIncludes("user")) {
        console.log("User view");
        showUserView();
      }
      if (session.rolesIncludes("mentor")) {
        console.log("Peer mentor view");
        showPeerMentorView();
      }
      if (session.rolesIncludes("admin")) {
        console.log("Admin view");
        showAdminView();
      }
    });

    onUnmounted(() => {
      window.removeEventListener("resize", checkMobile);
    });

    const handleLogout = async () => {
      try {
        // 1) Backend first due to auth token
        try {
          await authApi.logout();
          console.log("backend logout");
        } catch (e) {
          console.warn(
            "Backend logout failed; proceeding with local logout.",
            e,
          );
        }

        // 2) Frontend cleanup second
        session.logout();
        console.log("frontend logout");

        // Close mobile drawer & unlock body scroll
        closeNavMenu();

        // 3) Navigate last
        router.push({ path: "/login" });
      } catch (error) {
        console.error("Logout failed:", error);

        // Failsafe
        session.logout();
        closeNavMenu();
        router.push({ path: "/login" });
      }
    };

    return {
      isMobile,
      dropdownOpen,
      chatHistory,
      toggleDropdown,
      selectChatHistory,
      selectCurrentChat,
      formatSessionDate,
      isUserView,
      isPeerMentorView,
      isAdminView,
      showUserView,
      showPeerMentorView,
      showAdminView,
      selectView,
      handleNavClick,
      closeNavMenu,
      loading,
      session,
      isInChatPage,
      handleLogout,
    };
  },
};
</script>

<style scoped>
.sidenav-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.loading-message {
  text-align: center;
  font-size: 1.2em;
  color: #666;
}

.menu-icon {
  flex: 0 0 auto;
  margin: 0.5em;
  font-size: 24px;
  cursor: pointer;
  color: var(--primary-color);
  transition: color 0.3s ease;
}

.menu-icon:hover {
  color: var(--primary-color);
}

.sidenav-content {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  border-radius: 10px;
}

nav {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 1em;
  text-align: left;
}

nav h6 {
  margin-bottom: 0.25em;
  font-style: italic;
  text-decoration: none;
  white-space: nowrap;
}

nav a {
  margin-bottom: 0.25em;
  color: #060606;
  text-decoration: none;
  white-space: nowrap;
}

nav a:hover {
  text-decoration: underline;
}

nav a i {
  display: inline-block;
  margin-right: 0.25em;
}

nav hr {
  margin: 0.5em 0 0.75em 0;
  border: 0;
  height: 2px;
  background: #000000;
}

nav button {
  padding: 10px 20px;
  font-size: 16px;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: white;
  cursor: pointer;
  margin-right: 10px;
  min-width: 5em;
}

.settings {
  margin-top: auto;
  font-weight: bold;
}
.icon-spacing {
  margin-right: 8px;
}
.close-nav {
  display: none;
  margin-top: 1em;
  font-weight: bold;
  color: black;
  cursor: pointer;
}

.debug-toggle {
  padding: 0 1em 1em 1em;
}

.debug-toggle-switch {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.debug-toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 34px;
  transition: 0.4s;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 1px;
  background-color: #ccc;
  border-radius: 50%;
  transition: 0.4s;
}

input:checked + .toggle-slider:before {
  background-color: #1f6dca;
}

input:checked + .toggle-slider {
  background-color: white;
}

input:focus + .toggle-slider {
  box-shadow: 0 0 1px #1f6dca;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

.toggle-label {
  margin-left: 10px;
  font-size: 0.9em;
}

@media (max-width: 768px) {
  /* collapse the sidebar so it doesn't take horizontal space */
  .sidenav-menu {
    width: 0;
    min-width: 0;
  }

  /* float the hamburger so it appears "inside" the header area */
  .menu-icon {
    position: fixed;
    top: 20px;
    left: 3px;
    z-index: 1101; /* above header and chat */
    font-size: 22px;
    color: #ffffff;
    /* background: rgb(var(--background-color-rgb, 233 229 214) / 0.9); */
    background: var(--primary-color);
    padding: 6px 8px;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgb(0 0 0 / 0.15);
  }

  .sidenav-content {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: color-mix(
      in srgb,
      var(--background-color) 90%,
      transparent
    ); /* Slightly transparent background */
    z-index: 1000;
    overflow-y: auto;
    padding: 1em;
  }

  /* force-hide the debug toggle in case of stale markup */
  .debug-toggle {
    display: none !important;
  }

  nav {
    position: relative;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 5px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1001;
    font-size: 20px;
    overflow-y: auto;
    padding: 1em;
  }

  /* smaller, tidy Logout on the sheet */
  nav button {
    padding: 10px 16px;
    font-size: 14px;
    border-radius: 10px;
    min-width: 0;
  }

  nav h6 {
    font-size: 1em;
  }

  nav a {
    font-size: 0.9em;
  }

  nav a:hover {
    background-color: #f1f1f1;
  }
  .close-nav {
    display: block;
  }
}
</style>
